import { Link, useParams, Navigate } from "react-router-dom";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import FAQSection from "@/components/FAQSection";
import { FadeUp, MaskLine } from "@/components/motion";
import { SERVICES } from "@/data/content";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  return (
    <main className="pt-20" data-testid={`service-page-${service.slug}`}>
      <SEO
        title={service.title}
        description={`${service.tagline} ${service.priceNote}`}
        path={`/services/${service.slug}`}
      />
      <section className="relative overflow-hidden bg-navy grain">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-8 py-16 sm:py-24 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-amber-500">
              Services · {service.name}
            </p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tight leading-[1.08] text-white">
              <MaskLine delay={0.1}>{service.title}</MaskLine>
            </h1>
            <FadeUp delay={0.3}>
              <p className="mt-4 text-base sm:text-lg text-slate-300">{service.tagline}</p>
            </FadeUp>
            <FadeUp delay={0.45} className="mt-8">
              <Link to="/quote" data-testid="service-quote-btn"
                className="cta-gleam inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-navy">
                Get a {service.name} Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
            </FadeUp>
          </div>
          <FadeUp delay={0.3}>
            <div className="overflow-hidden rounded-3xl border border-white/10">
              <img src={service.image} alt={service.imageAlt} className="h-64 sm:h-80 w-full object-cover" />
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-16 sm:py-24 grid gap-14 lg:grid-cols-[1.3fr_1fr]">
        <div>
          <FadeUp>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-navy">How we do it</h2>
          </FadeUp>
          <FadeUp delay={0.1} className="mt-6 space-y-4">
            {service.intro.map((p, i) => (
              <p key={i} className="text-base leading-relaxed text-slate-600">{p}</p>
            ))}
          </FadeUp>
          <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-2">
            {service.process.map((step, i) => (
              <FadeUp key={step.t} delay={i * 0.06} className="bg-white p-8">
                <p className="font-display text-3xl font-black tracking-tighter text-amber-500">0{i + 1}</p>
                <h3 className="mt-3 font-display text-lg font-bold text-navy">{step.t}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{step.d}</p>
              </FadeUp>
            ))}
          </div>
        </div>
        <div className="space-y-8">
          <FadeUp className="rounded-2xl border border-slate-200 bg-white p-8">
            <h3 className="font-display text-lg font-bold text-navy">What's included</h3>
            <ul className="mt-5 space-y-3">
              {service.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-slate-600">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" /> {f}
                </li>
              ))}
            </ul>
          </FadeUp>
          <FadeUp delay={0.1} className="rounded-2xl bg-navy grain p-8">
            <p className="text-xs font-mono uppercase tracking-widest text-amber-500">Pricing guide</p>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">{service.priceNote}</p>
            <Link to="/quote" data-testid="service-aside-quote-btn"
              className="cta-gleam mt-6 inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-navy">
              Get my written quote <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
      </section>

      <FAQSection slug={service.slug} />
    </main>
  );
}
